// src/data/amazonFees.js
// Amazon US marketplace fee reference data.
// Last updated: 2026-04-10 — source: sellercentral.amazon.com
// Rates are approximations of published Amazon fee schedules. Update when Amazon changes its fee structure.

// Referral fees by product category.
// Most categories use a flat `rate`. Some (Beauty, Jewelry) use a `tieredRate` array
// where the first tier matching `salePrice < maxPrice` applies.
export const REFERRAL_FEES = [
  { id: 'automotive',     name: 'Automotive & Powersports', rate: 0.12, minFee: 0.30 },
  { id: 'baby',           name: 'Baby Products',            rate: 0.15, minFee: 0.30 },
  {
    id: 'beauty',
    name: 'Beauty, Health & Personal Care',
    tieredRate: [
      { maxPrice: 10,       rate: 0.08 },
      { maxPrice: Infinity, rate: 0.15 },
    ],
    minFee: 0.30,
  },
  { id: 'books',          name: 'Books',                    rate: 0.15, minFee: 0.30 },
  { id: 'clothing',       name: 'Clothing & Accessories',   rate: 0.17, minFee: 0.30 },
  { id: 'electronics',    name: 'Consumer Electronics',     rate: 0.08, minFee: 0.30 },
  { id: 'electronicsAcc', name: 'Electronics Accessories',  rate: 0.15, minFee: 0.30 },
  { id: 'grocery',        name: 'Grocery & Gourmet Food',   rate: 0.08, minFee: 0.30 }, // 8% under $15, 15% at/above — simplified to 8% as common case
  { id: 'home',           name: 'Home & Kitchen',           rate: 0.15, minFee: 0.30 },
  { id: 'industrial',     name: 'Industrial & Scientific',  rate: 0.12, minFee: 0.30 },
  {
    id: 'jewelry',
    name: 'Jewelry',
    tieredRate: [
      { maxPrice: 250,      rate: 0.20 },
      { maxPrice: Infinity, rate: 0.05 },
    ],
    minFee: 0.30,
  },
  { id: 'kitchen',        name: 'Kitchen',                  rate: 0.15, minFee: 0.30 },
  { id: 'lawnGarden',     name: 'Lawn & Garden',            rate: 0.15, minFee: 0.30 },
  { id: 'luggage',        name: 'Luggage & Travel',         rate: 0.15, minFee: 0.30 },
  { id: 'music',          name: 'Music, CDs & Vinyl',       rate: 0.15, minFee: 0.30 },
  { id: 'office',         name: 'Office Products',          rate: 0.15, minFee: 0.30 },
  { id: 'pets',           name: 'Pet Supplies',             rate: 0.15, minFee: 0.30 },
  { id: 'shoes',          name: 'Shoes, Handbags & Sunglasses', rate: 0.15, minFee: 0.30 },
  { id: 'sports',         name: 'Sports & Outdoors',        rate: 0.15, minFee: 0.30 },
  { id: 'tools',          name: 'Tools & Home Improvement', rate: 0.15, minFee: 0.30 },
  { id: 'toys',           name: 'Toys & Games',             rate: 0.15, minFee: 0.30 },
  { id: 'videoGames',     name: 'Video Games',              rate: 0.15, minFee: 0.30 },
  { id: 'watches',        name: 'Watches',                  rate: 0.16, minFee: 0.30 },
  { id: 'everythingElse', name: 'Everything Else',          rate: 0.15, minFee: 0.30 },
];

// FBA fulfillment fees by size tier and weight band.
// For a given tier, the first entry with `maxOz >= weightOz` applies.
// `storageType` feeds into the monthly storage fee calculation.
export const FBA_SIZE_TIERS = [
  {
    id: 'small_standard',
    name: 'Small Standard (≤1 lb, ≤15×12×0.75")',
    maxWeightOz: 16,
    maxDims: { longest: 15, median: 12, shortest: 0.75 },
    storageType: 'standard',
    fees: [
      { maxOz: 4,  fee: 3.06 },
      { maxOz: 8,  fee: 3.15 },
      { maxOz: 12, fee: 3.24 },
      { maxOz: 16, fee: 3.40 },
    ],
  },
  {
    id: 'large_standard',
    name: 'Large Standard (≤20 lb, ≤18×14×8")',
    maxWeightOz: 320,
    maxDims: { longest: 18, median: 14, shortest: 8 },
    storageType: 'standard',
    fees: [
      { maxOz: 4,   fee: 3.43 },
      { maxOz: 8,   fee: 3.54 },
      { maxOz: 12,  fee: 3.65 },
      { maxOz: 16,  fee: 3.87 },
      { maxOz: 24,  fee: 4.41 },
      { maxOz: 48,  fee: 4.68 },
      { maxOz: 72,  fee: 5.06 },
      { maxOz: 96,  fee: 5.40 },
      { maxOz: 160, fee: 5.69 }, // +$0.08/lb over 1.5 lb — simplified bands
      { maxOz: 320, fee: 7.17 },
    ],
  },
  {
    id: 'large_bulky',
    name: 'Large Bulky (≤50 lb, ≤59×33×33")',
    maxWeightOz: 800,
    maxDims: { longest: 59, median: 33, shortest: 33 },
    storageType: 'oversize',
    fees: [
      { maxOz: 800, fee: 9.73 }, // flat base + ~$0.42/lb — simplified to midpoint
    ],
  },
  {
    id: 'extra_large',
    name: 'Extra Large (>50 lb or oversized)',
    maxWeightOz: Infinity,
    maxDims: null,
    storageType: 'oversize',
    fees: [
      { maxOz: Infinity, fee: 26.33 }, // varies significantly; user should confirm with Amazon tool
    ],
  },
];

// Monthly inventory storage fees — $/cubic foot/month.
// Amazon charges higher rates Oct–Dec (holiday season).
export const STORAGE_FEES = {
  standard: { janSep: 0.87, octDec: 2.40 },
  oversize: { janSep: 0.56, octDec: 1.40 },
};
